/**
 * Shared influencer data for the dashboard. Fetched once and kept in state so navigating
 * between the list and a detail page (and back) never refetches. Save patches the cache.
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { billingApi } from './api'
import { InfluencerDetail } from './types'

interface InfluencersContextValue {
    influencers: InfluencerDetail[]
    loading: boolean
    error: string | null
    loaded: boolean
    ensureLoaded: () => void
    refresh: () => Promise<void>
    getById: (id: string) => InfluencerDetail | undefined
    applyUpdate: (id: string, patch: Partial<InfluencerDetail>) => void
    applyCreate: (influencer: InfluencerDetail) => void
    applyDelete: (id: string) => void
}

const InfluencersContext = createContext<InfluencersContextValue | null>(null)

export function useInfluencers(): InfluencersContextValue {
    const ctx = useContext(InfluencersContext)
    if (!ctx) throw new Error('useInfluencers must be used within InfluencersProvider')
    return ctx
}

/**
 * Layout route element: provides the influencer cache to the list + detail routes nested
 * under it via <Outlet />. Stays mounted while anywhere under /billing/influencers.
 */
export default function InfluencersLayout() {
    const [influencers, setInfluencers] = useState<InfluencerDetail[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [loaded, setLoaded] = useState(false)
    const inFlight = useRef(false)

    const refresh = useCallback(async () => {
        if (inFlight.current) return
        inFlight.current = true
        try {
            setLoading(true)
            setError(null)
            const data = await billingApi.getInfluencers()
            setInfluencers(data)
            setLoaded(true)
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch influencers')
        } finally {
            setLoading(false)
            inFlight.current = false
        }
    }, [])

    const ensureLoaded = useCallback(() => {
        if (!loaded && !inFlight.current) void refresh()
    }, [loaded, refresh])

    const getById = useCallback((id: string) => influencers.find((inf) => inf.id === id), [influencers])

    const applyUpdate = useCallback((id: string, patch: Partial<InfluencerDetail>) => {
        setInfluencers((prev) => prev.map((inf) => (inf.id === id ? { ...inf, ...patch } : inf)))
    }, [])

    const applyCreate = useCallback((influencer: InfluencerDetail) => {
        setInfluencers((prev) => [...prev, influencer].sort((a, b) => a.name.localeCompare(b.name)))
    }, [])

    const applyDelete = useCallback((id: string) => {
        setInfluencers((prev) => prev.filter((inf) => inf.id !== id))
    }, [])

    return (
        <InfluencersContext.Provider value={{ influencers, loading, error, loaded, ensureLoaded, refresh, getById, applyUpdate, applyCreate, applyDelete }}>
            <Outlet />
        </InfluencersContext.Provider>
    )
}
